import { useState, useEffect, useRef } from "react";
import { PAGES, initialState, modelEffects, clamp } from "../data/constants";
import { getEnding, generatePhoneContent } from "../data/gameLogic";
import { generateNextEvent } from "../data/eventEngine";
import { normalEvents } from "../data/events";

// 接收 changePage 方法，用来在引擎内部控制页面跳转
export default function useGameEngine(changePage) {
  const [state, setState] = useState(initialState);
  const [event, setEvent] = useState(normalEvents[0]);
  const [ending, setEnding] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const [phoneData, setPhoneData] = useState({ todayTrends: [], historyTrends: [], sms: [], email: [] });
  const [unread, setUnread] = useState({ mail: 0, sms: 0, news: 0, settings: 0 });
  const [unreadSenders, setUnreadSenders] = useState({});

  const [hackState, setHackState] = useState(null);
  const hackStateRef = useRef(null);

  // --- 存档与天赋系统 ---
  const [karma, setKarma] = useState(() => parseInt(localStorage.getItem("mj_karma") || "0"));
  const [unlockedEndings, setUnlockedEndings] = useState(() => JSON.parse(localStorage.getItem("mj_endings") || "[]"));
  const [selectedTalents, setSelectedTalents] = useState([]);

  // --- 终端日志系统 ---
  const [logs, setLogs] = useState([
    { id: 1, text: "# 码奸 OS (Ma-Jian OS) 核心引导程序 v1.0", color: "#888" },
    { id: 2, text: "# 正在接管大厂基础设施权限...", color: "#888" }
  ]);
  const terminalEndRef = useRef(null);

  useEffect(() => {
    hackStateRef.current = hackState;
  }, [hackState]);

  const hackTarget = hackState ? hackState.target : null;

  // Hack 倒计时引擎
  useEffect(() => {
    let timer;
    if (hackTarget) {
      timer = setInterval(() => {
        if (hackStateRef.current && hackStateRef.current.time > 0) {
          setHackState(prev => ({ ...prev, time: prev.time - 1 }));
        } else if (hackStateRef.current && hackStateRef.current.time <= 0) {
          clearInterval(timer);
          handleHackResult(false, hackStateRef.current);
        }
      }, 1000);
    }
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hackTarget]);

  // 死亡结算逻辑
  const endGame = (reason) => {
    setEnding(reason);
    changePage(PAGES.END); // 触发页面跳转

    const newKarma = Math.floor(state.round / 2);
    const totalKarma = karma + newKarma;
    const newEndings = [...new Set([...unlockedEndings, reason])];

    setKarma(totalKarma);
    setUnlockedEndings(newEndings);
    localStorage.setItem("mj_karma", totalKarma.toString());
    localStorage.setItem("mj_endings", JSON.stringify(newEndings));
  };

  const handleToggleTalent = (talent) => {
    if (selectedTalents.includes(talent.id)) {
      setSelectedTalents(prev => prev.filter(id => id !== talent.id));
      setKarma(prev => prev + talent.cost);
    } else {
      if (karma >= talent.cost) {
        setSelectedTalents(prev => [...prev, talent.id]);
        setKarma(prev => prev - talent.cost);
      } else {
        alert("业力不足！请多去打几遍工体验人生。");
      }
    }
  };

  const formatEffect = (effect, sanChange, moneyChange) => {
    let str = "";
    if (effect?.tech) str += `技术${effect.tech > 0 ? '+' : ''}${effect.tech} `;
    if (effect?.industry) str += `就业${effect.industry > 0 ? '+' : ''}${effect.industry} `;
    if (effect?.stability) str += `稳定${effect.stability > 0 ? '+' : ''}${effect.stability} `;
    if (effect?.self) str += `饭碗${effect.self > 0 ? '+' : ''}${effect.self} `;
    if (sanChange !== 0) str += `San${sanChange > 0 ? '+' : ''}${sanChange} `;
    if (moneyChange !== 0) str += `存款${moneyChange > 0 ? '+' : ''}${moneyChange} `;
    if (effect?.publicOpinion) str += `舆论${effect.publicOpinion > 0 ? '+' : ''}${effect.publicOpinion} `;
    if (effect?.rels?.boss) str += `老板好感${effect.rels.boss > 0 ? '+' : ''}${effect.rels.boss} `;
    if (effect?.rels?.team) str += `团队好感${effect.rels.team > 0 ? '+' : ''}${effect.rels.team} `;
    if (effect?.rels?.family) str += `家人好感${effect.rels.family > 0 ? '+' : ''}${effect.rels.family} `;
    return str.trim() || "无明显数值波动";
  };

  const handleModelSelect = (model) => {
    const effect = modelEffects[model];
    const initialMoneyBonus = selectedTalents.includes("rich") ? 50000 : 0;
    const nextState = {
      ...state,
      tech: clamp(state.tech + (effect.tech || 0)),
      industry: clamp(state.industry + (effect.industry || 0)),
      stability: clamp(state.stability + (effect.stability || 0)),
      self: clamp(state.self + (effect.self || 0)),
      money: state.money + initialMoneyBonus,
      model,
    };

    setLogs(prev => [...prev, { id: Date.now(), text: `> 成功挂载 [${model.toUpperCase()}] 引擎，开始祸害代码库...`, color: "#4caf50" }]);
    const initData = generatePhoneContent(nextState);

    setPhoneData({ todayTrends: initData.todayTrends || [], historyTrends: [], sms: initData.sms || [], email: initData.email || [] });
    setUnread({ mail: (initData.email || []).length, sms: (initData.sms || []).length, news: (initData.todayTrends || []).length, settings: 0 });

    const initialUnreadSenders = {};
    (initData.sms || []).forEach(s => { initialUnreadSenders[s.sender] = true; });
    setUnreadSenders(initialUnreadSenders);
    setState(nextState);
    changePage(PAGES.GAME);
  };

  const buyItem = (name, price, sanRestore) => {
    if (state.money >= price) {
      setState(prev => ({ ...prev, money: prev.money - price, san: clamp(prev.san + sanRestore) }));
      setLogs(prev => [...prev, { id: Date.now(), text: `> 💸 消费 ¥${price} 购买 [${name}]，心情变好点了。`, color: "#ff9800" }]);
    } else {
      setLogs(prev => [...prev, { id: Date.now(), text: `> ❌ 余额不足！无法购买 [${name}]，请继续打工！`, color: "#f44336" }]);
    }
  };

  const handleHackResult = (isWin, stateToUse) => {
    setHackState(null);
    const effect = isWin ? stateToUse.successEffect : stateToUse.failEffect;
    const text = isWin ? "【Hack 成功】手速惊人，底层逻辑覆写完成！" : "【Hack 失败】手速太慢，系统防线已被完全击穿！";
    const fakeChoice = { text, effect, logText: `> 💻 终端覆写协议: ${isWin ? '✅ SUCCESS' : '❌ FAILED'}`, logColor: isWin ? '#4caf50' : '#f44336' };
    choose(fakeChoice, true);
  };

  // ⚠️ 将你 App.js 原本的 choose 和 handlePhoneAction 函数的剩余代码原封不动粘贴到这里 ⚠️
  const choose = async (choice, isFromHack = false) => {
    if (isGenerating) return;

    // 🌟 Hack 拦截：如果是敲代码选项，冻结常规流程，激活终端！
    if (choice.isHack && !isFromHack) {
      setHackState({
        target: choice.hackCommand,
        time: choice.hackTime,
        input: "",
        successEffect: choice.effect,
        failEffect: choice.failEffect
      });
      return;
    }

    // 🌟 核心拦截器：动态修改 effect
    let effect = { ...(choice.effect || {}) };

    if (selectedTalents.includes("shifung") && effect.stability < 0) {
      effect.stability = Math.floor(effect.stability / 2); // 甩锅大师：背锅减半
    }
    if (selectedTalents.includes("evil") && effect.publicOpinion < 0) {
      effect.san = (effect.san || 0) + Math.abs(effect.publicOpinion); // 天生坏种：被骂反而开心
    }

    const newTags = choice.tag ? [...state.tags, choice.tag] : state.tags;
    const nextRound = state.round + 1;
    const monthStep = Math.floor(Math.random() * 5) + 1;

    let sanChange = (effect.san || 0) - 5;
    // 🌟 天赋拦截：富二代每周额外扣 San
    if (selectedTalents.includes("rich")) {
      sanChange -= 2;
    }
    let newSan = clamp(state.san + sanChange);
    let isPayday = (nextRound % 4 === 1 && state.round > 0);
    let income = effect.money || 0;
    if (isPayday) income += 25000;
    let newMoney = state.money + income;

    const effectStr = formatEffect(effect, sanChange, effect.money || 0);
    let newLogs = [{
      id: Date.now(),
      // 🌟 如果 choice 里带了特殊日志（Hack结果），就用它
      text: choice.logText || `[Week ${state.round}] 执行: ${choice.text} | 结果: ${effectStr}`,
      color: choice.logColor || "#2196f3"
    }];

    if (isPayday) {
      newLogs.push({ id: Date.now() + 1, text: `[💰 叮咚] 月底工资到账：¥25,000！资本家的施舍让你暂时不想跳楼了。`, color: "#4caf50" });
    }

    setLogs(prev => [...prev, ...newLogs]);

    const newStocks = JSON.parse(JSON.stringify(state.stocks));
    Object.keys(newStocks).forEach(key => {
      const stock = newStocks[key];
      // 自然波动：每回合 -10% 到 +15% 之间
      let changePercent = (Math.random() * 0.25) - 0.10;

      // 🌟 内幕交易影响：如果当前事件的 effect 里带了股票影响，直接强力干预！
      if (effect.stocks && effect.stocks[key]) {
        changePercent += effect.stocks[key] / 100;
      }

      let newPrice = Math.max(1, Math.floor(stock.price * (1 + changePercent)));
      stock.history.push(newPrice);
      if (stock.history.length > 10) stock.history.shift(); // 最多存最近10回合趋势
      stock.price = newPrice;
    });

    const currentSnapshot = {
      round: state.round,
      gameMonths: state.gameMonths,
      eventTitle: event.title,
      choiceText: choice.text,
      tech: state.tech,
      industry: state.industry,
      stability: state.stability,
      self: state.self,
      san: state.san
    };

    const newState = {
      ...state,
      tech: clamp(state.tech + (effect.tech || 0)),
      industry: clamp(state.industry + (effect.industry || 0)),
      stability: clamp(state.stability + (effect.stability || 0)),
      self: clamp(state.self + (effect.self || 0)),
      money: newMoney,
      publicOpinion: clamp(state.publicOpinion + (effect.publicOpinion || 0)),
      san: newSan,
      round: nextRound,
      gameMonths: state.gameMonths + monthStep,
      tags: newTags,
      playedEvents: [...state.playedEvents, event.id],
      rels: { // 🌟 新增：更新 NPC 好感度
        boss: clamp(state.rels.boss + (effect.rels?.boss || 0)),
        team: clamp(state.rels.team + (effect.rels?.team || 0)),
        family: clamp(state.rels.family + (effect.rels?.family || 0)),
      },
      stocks: newStocks, // 🌟 把更新后的股市塞入新状态
      historyLog: [...(state.historyLog || []), currentSnapshot],
    };

    if (nextRound > 12 || newState.self <= 0 || newState.stability <= 5 || newSan <= 0) {
      let reason = "";
      if (newSan <= 0) {
        reason = "你的 San 值已归零。你看着屏幕上密密麻麻的代码，突然狂笑起来，随后被救护车拉走，彻底告别了大厂...";
      } else {
        reason = getEnding(newState);
      }

      endGame(reason); // 🌟 调用真正的结算函数！存业力！
      return;
    }

    const newContent = generatePhoneContent(newState);

    const allOldTrends = [...(phoneData.todayTrends || []), ...(phoneData.historyTrends || [])];
    const safeTodayTrends = newContent.todayTrends || [];
    const newNews = safeTodayTrends.filter(n => !allOldTrends.some(old => old.id === n.id));

    let nextToday = phoneData.todayTrends || [];
    let nextHistory = phoneData.historyTrends || [];

    if (newNews.length > 0) {
      nextHistory = [...(phoneData.todayTrends || []), ...(phoneData.historyTrends || [])];
      nextToday = newNews;
    }

    const safeSms = newContent.sms || [];
    const safeEmail = newContent.email || [];

    const newSms = safeSms.filter(s => {
      if (unreadSenders[s.sender]) return false;
      return !(phoneData.sms || []).some(old => old.id === s.id);
    });

    const newEmail = safeEmail.filter(e => !(phoneData.email || []).some(old => old.id === e.id));

    if (newSms.length > 0) {
      setUnreadSenders(prev => {
        const next = { ...prev };
        newSms.forEach(s => next[s.sender] = true);
        return next;
      });
    }

    if (newState.stability < 40 && state.stability >= 40) {
      setLogs(prev => [...prev, { id: Date.now() + 2, text: `[WARN] 检测到核心服务崩溃，大量用户在微博要求退钱！`, color: "#f44336" }]);
    }

    setPhoneData({
      todayTrends: nextToday,
      historyTrends: nextHistory,
      sms: [...newSms, ...(phoneData.sms || [])],
      email: [...newEmail, ...(phoneData.email || [])]
    });

    setUnread(prev => ({
      mail: prev.mail + newEmail.length,
      sms: prev.sms + newSms.length,
      news: newNews.length > 0 ? newNews.length : prev.news,
      settings: prev.settings
    }));

    setState(newState);
    setIsGenerating(true);
    setLogs(prev => [...prev, { id: Date.now() + 3, text: `> 正在接入神经中枢，推演下一周行业局势...`, color: "#ff9800" }]);

    try {
      const result = await generateNextEvent(newState);

      if (result.status === "emergency") {
        setLogs(prev => [...prev, { id: Date.now() + 4, text: `[🚨 红色警报] 突发状况切断了推演进程！`, color: "#f44336" }]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        setLogs(prev => [...prev, { id: Date.now() + 4, text: `> 局势推演完毕，新的危机已出现。`, color: "#4caf50" }]);
      }

      setEvent(result.event);
    } catch (error) {
      console.error(error);
      setLogs(prev => [...prev, { id: Date.now() + 4, text: `> 引擎崩溃！启用本地降级预案...`, color: "#f44336" }]);
      setEvent(normalEvents[0]);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handlePhoneAction = (effect) => {
    if (!effect) return;

    if (effect.actionType === 'MARK_AS_READ') {
      const countToReduce = effect.count || 1;
      setUnread(prev => ({
        ...prev,
        [effect.app]: Math.max(0, prev[effect.app] - countToReduce)
      }));

      if (effect.app === 'sms' && effect.sender && effect.unblock) {
        setUnreadSenders(prev => {
          const next = { ...prev };
          delete next[effect.sender];
          return next;
        });
      }
      return;
    }

    if (effect.actionType === 'REPLY_SMS') {
      if (effect.sender) {
        setUnreadSenders(prev => {
          const next = { ...prev };
          delete next[effect.sender];
          return next;
        });
      }
      // 🌟 注意：这里原本有一个 return; 我们去掉了！
      // 因为我们需要让后续带有数值的 effect 继续往下走结算
    }

    if (effect.actionType === 'TRADE_STOCK') {
      const { stockKey, amount, totalCost } = effect;
      setState(prev => ({
        ...prev,
        money: prev.money - totalCost,
        portfolio: {
          ...prev.portfolio,
          [stockKey]: (prev.portfolio[stockKey] || 0) + amount
        }
      }));
      setLogs(prev => [...prev, { id: Date.now(), text: `> 📱 操作：${amount > 0 ? '买入' : '卖出'} [${stockKey}] ${Math.abs(amount)}股，金额：¥${Math.abs(totalCost)}`, color: "#2196f3" }]);
      return;
    }

    // ==========================================
    // 🌟 核心修复：处理手机带来的所有数值变化
    // ==========================================
    const hasChanges = effect.tech || effect.industry || effect.stability || effect.self || effect.san || effect.money || effect.publicOpinion || effect.rels;

    if (hasChanges) {
      // 1. 在终端面板里输出日志，让你看到手机互动的反馈
      const effectStr = formatEffect(effect, effect.san || 0, effect.money || 0);
      setLogs(prev => [...prev, { id: Date.now() + Math.random(), text: `> 📱 手机交互: ${effectStr}`, color: "#9c27b0" }]);

      // 2. 真实修改所有的底层数值（把遗漏的 rels 补上）
      setState(prevState => ({
        ...prevState,
        tech: clamp(prevState.tech + (effect.tech || 0)),
        industry: clamp(prevState.industry + (effect.industry || 0)),
        stability: clamp(prevState.stability + (effect.stability || 0)),
        self: clamp(prevState.self + (effect.self || 0)),
        publicOpinion: clamp(prevState.publicOpinion + (effect.publicOpinion || 0)),
        money: prevState.money + (effect.money || 0),
        san: clamp(prevState.san + (effect.san || 0)),

        // 🌟 新增：好感度结算系统
        rels: {
          boss: clamp((prevState.rels?.boss || 50) + (effect.rels?.boss || 0)),
          team: clamp((prevState.rels?.team || 50) + (effect.rels?.team || 0)),
          family: clamp((prevState.rels?.family || 50) + (effect.rels?.family || 0)),
        }
      }));
    }
  };

  // 最后，暴露出所有供 UI 调用的状态和方法
  return {
    state, event, ending, isGenerating, phoneData, unread, hackState,
    karma, unlockedEndings, selectedTalents, logs, terminalEndRef, hackStateRef,
    handleToggleTalent, handleModelSelect, buyItem, handleHackResult, choose, handlePhoneAction, setHackState
  };
}